import React, { useState, useEffect } from 'react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import { FaUserFriends, FaBookOpen, FaClipboardCheck, FaChartLine } from 'react-icons/fa';
import { DataGrid } from '@mui/x-data-grid';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const Home = () => {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    classe: 'Toutes',
    periode: 'Tous',
    matiere: 'Toutes',
    eleve: 'Tous',
  });

  useEffect(() => {
    // Simuler les données globales (non filtrées)
    const staticData = {
      classes: ['6ème', '5ème', '4ème'],
      periodes: ['Trimestre 1', 'Trimestre 2', 'Trimestre 3'],
      matieres: ['Maths', 'Français', 'Science'],
      eleves: ['Alice', 'Babacar', 'Fatou', 'Issa'],
      performance: [
        { id: 1, nom: 'Alice', classe: '6ème', matiere: 'Maths', periode: 'Trimestre 1', cours: 15, exercices: 60, moyenne: 14.2 },
        { id: 2, nom: 'Babacar', classe: '6ème', matiere: 'Français', periode: 'Trimestre 2', cours: 12, exercices: 45, moyenne: 12.8 },
        { id: 3, nom: 'Fatou', classe: '5ème', matiere: 'Maths', periode: 'Trimestre 1', cours: 18, exercices: 68, moyenne: 15.5 },
        { id: 4, nom: 'Issa', classe: '4ème', matiere: 'Science', periode: 'Trimestre 3', cours: 10, exercices: 34, moyenne: 11.7 },
      ],
      exercicesByClass: {
        '6ème – Maths': 40,
        '6ème – Français': 32,
        '5ème – Maths': 28,
        '4ème – Science': 50,
      },
      progressBySubject: {
        Maths: 75,
        Français: 60,
        Science: 45,
      },
      onlineStudents: 25,
      lessonsToday: 120,
      examsToday: 8,
      lessonsProgressOverTime: [
        { jour: 'Lun', completées: 10 },
        { jour: 'Mar', completées: 15 },
        { jour: 'Mer', completées: 8 },
        { jour: 'Jeu', completées: 12 },
        { jour: 'Ven', completées: 20 },
      ],
      engagement: {
        tempsMoyen: { jour: 45, semaine: 320, mois: 1200 }, // en minutes
        tauxCompletion: [
          { nom: 'Alice', cours: 90, exercices: 80 },
          { nom: 'Babacar', cours: 70, exercices: 60 },
          { nom: 'Fatou', cours: 95, exercices: 85 },
          { nom: 'Issa', cours: 60, exercices: 50 },
        ],
        connexions: [
          { nom: 'Alice', total: 30 },
          { nom: 'Babacar', total: 18 },
          { nom: 'Fatou', total: 25 },
          { nom: 'Issa', total: 10 },
        ],
        heatmap: [
          // [jour, heure, nb connexions]
          { jour: 'Lun', heure: 10, connexions: 5 },
          { jour: 'Lun', heure: 14, connexions: 8 },
          { jour: 'Mar', heure: 11, connexions: 7 },
          { jour: 'Ven', heure: 16, connexions: 12 },
        ],
        inactifs: [
          { nom: 'Issa', jours: 15 },
        ],
      },
      comprehension: {
        tauxReussite: [
          { matiere: 'Maths', classe: '6ème', taux: 85 },
          { matiere: 'Français', classe: '5ème', taux: 78 },
          { matiere: 'Science', classe: '4ème', taux: 60 },
        ],
        plusEchoues: [
          { exercice: 'Exo 1 Maths', echecs: 6 },
          { exercice: 'Exo 2 Science', echecs: 8 },
        ],
        classement: [
          { nom: 'Fatou', moyenne: 15.5 },
          { nom: 'Alice', moyenne: 14.2 },
          { nom: 'Babacar', moyenne: 12.8 },
          { nom: 'Issa', moyenne: 11.7 },
        ],
        evolutionMoyennes: [
          { nom: 'Alice', t1: 13, t2: 14, t3: 15 },
          { nom: 'Babacar', t1: 12, t2: 13, t3: 13.5 },
          { nom: 'Fatou', t1: 15, t2: 15.5, t3: 16 },
          { nom: 'Issa', t1: 11, t2: 12, t3: 12 },
        ],
      },
      pedagogique: {
        devoirs: [
          { nom: 'Alice', rendus: 8, nonRendus: 2, retard: 1 },
          { nom: 'Babacar', rendus: 6, nonRendus: 4, retard: 3 },
        ],
        coursPopularite: [
          { cours: 'Chapitre 1', vues: 120 },
          { cours: 'Chapitre 2', vues: 30 },
          { cours: 'Chapitre 3', vues: 0 },
        ],
      },
      enseignants: {
        contenus: [
          { enseignant: 'M. Diop', ajoutes: 12 },
          { enseignant: 'Mme Ndiaye', ajoutes: 8 },
        ],
        tauxCompletion: [
          { enseignant: 'M. Diop', taux: 80 },
          { enseignant: 'Mme Ndiaye', taux: 70 },
        ],
        feedback: [
          { enseignant: 'M. Diop', note: 4.5 },
          { enseignant: 'Mme Ndiaye', note: 4.2 },
        ],
      },
      temporel: {
        activite: [
          { periode: 'Trimestre 1', activite: 120 },
          { periode: 'Trimestre 2', activite: 140 },
          { periode: 'Trimestre 3', activite: 110 },
        ],
        progressionMatiere: [
          { matiere: 'Maths', t1: 70, t2: 80, t3: 85 },
          { matiere: 'Français', t1: 60, t2: 65, t3: 70 },
        ],
      },
      alertes: {
        decrochage: ['Issa'],
        performants: ['Fatou'],
        coursPeuUtilises: ['Chapitre 3'],
      },
    };
    setData(staticData);
  }, []);

  if (!data) return <div>Chargement…</div>;

  // Appliquer les filtres
  const filteredPerformance = data.performance.filter(p =>
    (filters.classe === 'Toutes' || p.classe === filters.classe) &&
    (filters.periode === 'Tous' || p.periode === filters.periode) &&
    (filters.matiere === 'Toutes' || p.matiere === filters.matiere) &&
    (filters.eleve === 'Tous' || p.nom === filters.eleve)
  );

  // Données graphiques
  const barData = {
    labels: Object.keys(data.exercicesByClass),
    datasets: [
      {
        label: 'Exercices réalisés',
        data: Object.values(data.exercicesByClass),
        backgroundColor: '#198754',
      },
    ],
  };

  const pieData = {
    labels: Object.keys(data.progressBySubject),
    datasets: [
      {
        data: Object.values(data.progressBySubject),
        backgroundColor: ['#0d6efd', '#ffc107', '#dc3545'],
      },
    ],
  };

  const lineData = {
    labels: data.lessonsProgressOverTime.map(d => d.jour),
    datasets: [
      {
        label: 'Leçons complétées',
        data: data.lessonsProgressOverTime.map(d => d.completées),
        borderColor: '#0d6efd',
        tension: 0.3,
      },
    ],
  };

  const columns = [
    { field: 'nom', headerName: 'Élève', flex: 1 },
    { field: 'classe', headerName: 'Classe', flex: 1 },
    { field: 'matiere', headerName: 'Matière', flex: 1 },
    { field: 'periode', headerName: 'Période', flex: 1 },
    { field: 'cours', headerName: 'Cours', type: 'number', flex: 1 },
    { field: 'exercices', headerName: 'Exercices', type: 'number', flex: 1 },
    { field: 'moyenne', headerName: 'Moyenne', type: 'number', flex: 1 },
  ];

  return (
    <div className="container my-4">
      <h2 className="mb-4 text-center">Statistiques WILOFO (Filtres dynamiques)</h2>

      {/* Filtres */}
      <div className="row mb-4 g-2">
        {['classe', 'periode', 'matiere', 'eleve'].map((filter) => (
          <div className="col-md-3" key={filter}>
            <label className="form-label text-capitalize">{filter}</label>
            <select
              className="form-select"
              value={filters[filter]}
              onChange={e => setFilters({ ...filters, [filter]: e.target.value })}
            >
              <option value={filter === 'periode' ? 'Tous' : 'Toutes'}>
                {filter === 'periode' ? 'Tous' : 'Toutes'}
              </option>
              {data[filter === 'eleve' ? 'eleves' : filter + 's'].map((v, i) => (
                <option key={i}>{v}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {/* Cards */}
      <div className="row g-4 mb-5">
        <div className="col-md-3">
          <div className="card text-white bg-primary h-100 shadow">
            <div className="card-body d-flex align-items-center">
              <FaUserFriends size={40} className="me-3" />
              <div>
                <p>Élèves en ligne</p>
                <h2>{data.onlineStudents}</h2>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-white bg-success h-100 shadow">
            <div className="card-body d-flex align-items-center">
              <FaBookOpen size={40} className="me-3" />
              <div>
                <p>Cours aujourd’hui</p>
                <h2>{data.lessonsToday}</h2>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-white bg-warning h-100 shadow">
            <div className="card-body d-flex align-items-center">
              <FaClipboardCheck size={40} className="me-3" />
              <div>
                <p>Examens actifs</p>
                <h2>{data.examsToday}</h2>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-white bg-info h-100 shadow">
            <div className="card-body d-flex align-items-center">
              <FaChartLine size={40} className="me-3" />
              <div>
                <p>Moyenne filtrée</p>
                <h2>{filteredPerformance.length ? (filteredPerformance.reduce((a, b) => a + b.moyenne, 0) / filteredPerformance.length).toFixed(1) : '—'}</h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="row g-4 mb-5">
        <div className="col-md-6">
          <div className="card p-4 shadow-sm">
            <h5>Exercices par classe/matière</h5>
            <Bar data={barData} />
          </div>
        </div>
        <div className="col-md-6">
          <div className="card p-4 shadow-sm">
            <h5>Progrès par matière</h5>
            <Pie data={pieData} />
          </div>
        </div>
      </div>

      <div className="card p-4 mb-4 shadow-sm">
        <h5>Progression des leçons</h5>
        <Line data={lineData} />
      </div>

      {/* Alertes intelligentes */}
      {data.alertes.decrochage.length > 0 && (
        <div className="alert alert-danger">
          ⚠️ Élèves en décrochage : {data.alertes.decrochage.join(', ')}
        </div>
      )}
      {data.alertes.performants.length > 0 && (
        <div className="alert alert-success">
          🏅 Élèves très performants : {data.alertes.performants.join(', ')}
        </div>
      )}
      {data.alertes.coursPeuUtilises.length > 0 && (
        <div className="alert alert-warning">
          📌 Cours peu utilisés : {data.alertes.coursPeuUtilises.join(', ')}
        </div>
      )}

      {/* Engagement des élèves */}
      <div className="row g-4 mb-4">
        <div className="col-md-4">
          <div className="card p-3 shadow-sm">
            <h6>⏱️ Temps moyen par élève</h6>
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Période</th>
                  <th>Temps (min)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Jour</td>
                  <td>{data.engagement.tempsMoyen.jour}</td>
                </tr>
                <tr>
                  <td>Semaine</td>
                  <td>{data.engagement.tempsMoyen.semaine}</td>
                </tr>
                <tr>
                  <td>Mois</td>
                  <td>{data.engagement.tempsMoyen.mois}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card p-3 shadow-sm">
            <h6>📈 Taux de complétion</h6>
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Élève</th>
                  <th>Cours (%)</th>
                  <th>Exos (%)</th>
                </tr>
              </thead>
              <tbody>
                {data.engagement.tauxCompletion.map(e => (
                  <tr key={e.nom}>
                    <td>{e.nom}</td>
                    <td>{e.cours}</td>
                    <td>{e.exercices}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card p-3 shadow-sm">
            <h6>🔄 Connexions par élève</h6>
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Élève</th>
                  <th>Connexions</th>
                </tr>
              </thead>
              <tbody>
                {data.engagement.connexions.map(e => (
                  <tr key={e.nom}>
                    <td>{e.nom}</td>
                    <td>{e.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Classement des élèves */}
      <div className="card p-3 mb-4 shadow-sm">
        <h6>🏆 Classement général (top 3)</h6>
        <table className="table table-sm table-bordered mb-0 text-center align-middle">
          <thead>
            <tr>
              <th style={{width: "60px"}}>Place</th>
              <th>Élève</th>
              <th>Moyenne</th>
            </tr>
          </thead>
          <tbody>
            {data.comprehension.classement.slice(0, 3).map((e, idx) => (
              <tr key={e.nom} className={idx === 0 ? "table-success" : idx === 1 ? "table-primary" : "table-warning"}>
                <td>
                  {idx === 0 && <span>🥇</span>}
                  {idx === 1 && <span>🥈</span>}
                  {idx === 2 && <span>🥉</span>}
                </td>
                <td>{e.nom}</td>
                <td><strong>{e.moyenne}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tableau des élèves */}
      <div className="card p-4 shadow-sm">
        <h5 className="mb-3">Détails des élèves filtrés</h5>
        <div style={{ height: 300 }}>
          <DataGrid rows={filteredPerformance} columns={columns} pageSize={5} />
        </div>
      </div>
    </div>
  );
};

export default Home;
